import React from "react";
import Zergling from "../helpers/zergling";
import { digest } from "json-hash";
import { SwarmLoadingStart, SwarmLoadingDone, SwarmReceiveData, ReceiveUpdate, SwarmClearData } from "../actions/swarm";
import { broadcast, broadcastChannels } from "../helpers/broadcast";
import { store } from "../index/terminalApp";

/**
 * SwarmDataMixin is a HOC(Higher Order Component) which adds possibility to
 * load/unload data from swarm on component mount/unmount
 * To enable the functionality component should be "wrapped" by SwarmDataMixin, i.e. you should use
 * SwarmDataMixin({Component: YourComponent}) instead of just YourComponent.
 *
 * Depending on properties that composed component has corresponding data request/subscription to swarm will be made.
 *
 * Option 1 - one-time data request:
 * for just getting some initial data, component should have the following properties:
 *      swarmInitialDataRequest - the request object to query swarm for data.
 *      swarmInitialDataRequestCommand - initial data request command (default is "get")
 *      swarmDataKey - [optional] the key to keep the received data under in store. If not provided a hash of request/command will be used as key
 *
 * Option 2 - subscription:
 * in this case a subscription will be made, so when data changes, it will be updated in store.
 * On component unmount an unsubscribe request will be sent to swarm to stop getting updates.
 * for subscribing component should have the following properties:
 *
 *      swarmSubscriptionRequest - the request object to query swarm for data.
 *      swarmSubscriptionRequests - [optional] Array of swarmSubscriptionRequest (while using multiple requests)
 *
 *      swarmDataKey - [optional] the key to keep the received data under in store. If not provided a hash of request will be used as key
 *      swarmDataKeys - [optional] Array of swarmDataKey (while using multiple requests)
 *
 *      keepDataAfterUnsubscribe - [optional]. If true, data will be kept in store after unsubscribing
 *
 * the loaded data can be used in wrapped component by getting it from store.swarmData.data[this.props.swarmDataKey]
 * or with each key of this.props.swarmDataKeys if using multiple requests
 *
 * additionally, composed component will get a "resubscribe" method which can be called to unsubscribe(if subscribed)
 * and make the initial request again. It has an optional boolean argument "keepDataInStore". If set to true, component
 * subscription data in store won't be cleared before making the new request.
 *
 * @param {Object} ComposedComponent  The composed component object can have the following fields:
 *                  "Component" field - required. value is the component to be wrapped.
 *                  "ComponentWillMount" field - optional. composed component's "ComponentWillMount" method.
 *                  "ComponentWillReceiveProps" field - optional. composed component's "ComponentWillReceiveProps" method.
 * @constructor
 */
export var SwarmDataMixin = ComposedComponent =>
	class extends React.Component {
		constructor(props) {
			super(props);
			this.state = ComposedComponent.InitialState || {};

			if (ComposedComponent.ComponentWillMount) {
				this.UNSAFE_componentWillMount = ComposedComponent.ComponentWillMount;
			}

			if (ComposedComponent.ComponentWillReceiveProps) {
				this.UNSAFE_componentWillReceiveProps = ComposedComponent.ComponentWillReceiveProps;
			}
			this.subscriptionPromise = Promise.resolve();
			this.onReconnect = this.onReconnect.bind(this);
			this.time = 1;
			this.unmounted = false;
		}

		subscribe(subscriptionRequest, dataKey) {
			this.subscriptions = [];
			if (subscriptionRequest) {
				dataKey =
					dataKey ||
					`${getSwarmDataKeyForRequest(subscriptionRequest)}${
						this.uidPostFix ? this.uidPostFix : ""
					}`;
				var timeoutReject;
				try {
					this.meta.dataKey = dataKey;
				} catch {
					/* Data from swarm is not an object-like data structure */
				}

				const addToQueue = () => {
					if (this.unmounted) return;

					if (this.swarmDataKeys) this.swarmDataKeys.push(dataKey);
					else this.swarmDataKeys = [dataKey];
					if (this.swarmSubscriptionRequests)
						this.swarmSubscriptionRequests.push(subscriptionRequest);
					else this.swarmSubscriptionRequests = [subscriptionRequest];
				};

				this.subscriptionPromise = new Promise((resolve, reject) => {
					timeoutReject = setTimeout(function () {
						addToQueue();
						reject(new Error("promise subscribe timeout"));
					}, 10000);
					// eslint-disable-next-line react/prop-types
					this.props.dispatch(SwarmLoadingStart(dataKey));

					// setTimeout(() => {
					// 	try {
					// 		const state = store.getState();
					// 		const loadedState = state?.swarmData?.loaded;
					// 		const loadedData = state?.swarmData?.data;
					// 		console.log(
					// 			{
					// 				dataKey,
					// 				loaded: loadedState[dataKey],
					// 			},
					// 			"Checking data existence"
					// 		); // Gohar

					// 		if (!loadedState?.[dataKey] && loadedData?.[dataKey]) {
					// 			console.log(loadedData, loadedState, 'Checking existence failed'); // Esel Gohar
					// 			this.props.dispatch(SwarmLoadingDone(dataKey));
					// 		}
					// 	} catch (ex) {
					// 		console.log(ex, "exception on data check timer"); // Es karas chjnjes
					// 	}
					// }, 6000);

					Zergling.subscribe(
						subscriptionRequest,
						function (response) {
							// console.debug("swarm update:", response);
							setTimeout(() => {
								this.props.dispatch(
									ReceiveUpdate(response, dataKey, this.meta)
								); //eslint-disable-line react/prop-types
							}, 200);
						}.bind(this)
					)
						.then(
							function (response) {
								console.debug("swarm response:", response);
								clearTimeout(timeoutReject);
								resolve();
								// eslint-disable-next-line react/prop-types
								this.props.dispatch(SwarmLoadingDone(dataKey));
								if (typeof this.initialLoadCallback === "function") {
									this.initialLoadCallback(response.data);
								}
								// eslint-disable-next-line react/prop-types
								setTimeout(() => {
									this.props.dispatch(
										SwarmReceiveData(
											response.data,
											dataKey,
											this.meta
										)
									);
								}, 200);
								this.subscriptions.push(response.subid);
							}.bind(this)
						)
						.catch(
							function (ex) {
								console.debug("swarm exception:", ex);
								clearTimeout(timeoutReject);
								reject();
								addToQueue();
								this.props.dispatch(SwarmLoadingDone(dataKey)); //eslint-disable-line react/prop-types
							}.bind(this)
						);
				});
				this.subscriptionPromise.catch(ex => console.error("Caught A subscription ERROR", ex));
			} else if (this.swarmInitialDataRequest) {
				// just load some initial data
				let command = this.swarmInitialDataRequestCommand || "get";
				this.swarmDataKey = this.swarmDataKey || command + digest(this.swarmInitialDataRequest);
				// eslint-disable-next-line react/prop-types
				this.props.dispatch(SwarmLoadingStart(this.swarmDataKey));
				Zergling.get(this.swarmInitialDataRequest, command)
					.then(
						function (response) {
							console.debug("swarm response:", response);
							this.props.dispatch(SwarmLoadingDone(this.swarmDataKey)); // eslint-disable-line react/prop-types
							this.props.dispatch(
								SwarmReceiveData(
									this.takeFromResponse
										? response[this.takeFromResponse]
										: response.data,
									this.swarmDataKey,
									this.meta
								)
							); // eslint-disable-line react/prop-types
						}.bind(this)
					)
					.catch(
						function (ex) {
							console.debug("swarm exception:", ex);
							this.props.dispatch(SwarmLoadingDone(this.swarmDataKey)); //eslint-disable-line react/prop-types
						}.bind(this)
					);
			}
		}

		_unsubscribe(keepDataInStore = false) {
			this.unsubscribing = true;
			//this.subscriptionPromise = Promise.resolve();
			return this.subscriptionPromise
				.catch(ex =>
					console.error(
						"error in subscription Promiiiiiiiiiiiiiiiiiiiiiiiiiiiiiiiiiiiise:",
						ex
					)
				)
				.then(() => {
					console.debug("unsubscribing", this.subscriptions);
					if (this.subscriptions && this.subscriptions.length < 1) {
						console.debug("no subscriptions to unsubscribe");
						this.unsubscribing = false;
						return Promise.resolve();
					}
					return Zergling.unsubscribe(this.subscriptions)
						.catch(ex => console.warn("cannot unsubscribe:", ex))
						.then(() => {
							console.debug("_unsubscribed");
							if (!keepDataInStore) {
								this.swarmDataKeys
									? this.swarmDataKeys.forEach(item =>
											this.props.dispatch(
												SwarmClearData(item)
											)
									  )
									: this.props.dispatch(
											SwarmClearData(
												this.swarmDataKey
											)
									  ); //eslint-disable-line react/prop-types
							}
							this.subscriptions = [];
							this.unsubscribing = false;
						});
				});
		}

		onReconnect() {
			if (this.unmounted) return;

			setTimeout(() => {
				if (this.swarmSubscriptionRequests && this.swarmDataKeys) {
					this.swarmSubscriptionRequests.forEach((item, index) => {
						this.subscribe(item, this.swarmDataKeys[index]);
					});
				}
			}, 0);
		}

		componentDidMount() {
			broadcast.addEventListener(broadcastChannels.WSReconnect, this.onReconnect);

			setTimeout(() => {
				if (this.swarmSubscriptionRequests && this.swarmDataKeys) {
					this.swarmSubscriptionRequests.forEach((item, index) => {
						this.subscribe(item, this.swarmDataKeys[index]);
					});
				} else this.subscribe(this.swarmSubscriptionRequest, this.swarmDataKey);
			}, 0);
		}

		componentWillUnmount() {
			this._unsubscribe(this.keepDataAfterUnsubscribe);
			this.unmounted = true;
			broadcast.removeEventListener(broadcastChannels.WSReconnect, this.onReconnect);
		}

		resubscribe(keepDataInStore = false) {
			console.debug("resubscribing (swarmDataMixin)", this.subscriptions);
			this._unsubscribe(keepDataInStore)
				.then(
					function () {
						try {
							if (this.swarmSubscriptionRequests && this.swarmDataKeys) {
								this.swarmSubscriptionRequests.forEach(
									(item, index) => {
										this.subscribe(
											item,
											this.swarmDataKeys[index]
										);
									}
								);
							} else
								this.subscribe(
									this.swarmSubscriptionRequest,
									this.swarmDataKey
								);
						} catch (error) {
							console.error(
								"Thrown Promise Rejection Occured from Resubscribe (Subscribe part TRY CATCH )",
								error
							);
						}
					}.bind(this)
				)
				.catch(ex =>
					console.error(
						"Thrown Promise Rejection Occured from Resubscribe (Subscribe part)",
						ex
					)
				);
		}

		render() {
			return (
				<ComposedComponent.Component
					{...this.props}
					{...this.state}
					swarmDataKey={this.swarmDataKey}
					swarmDataKeys={this.swarmDataKeys}
				/>
			);
		}
	};

/**
 * Calculate unique hash for giver request object
 * @param {Object} request
 * @returns {String}
 */
export function getSwarmDataKeyForRequest(request) {
	return digest(request);
}
